import React, { useState, useEffect } from "react";
import { Menu as MenuIcon, X } from "lucide-react";
import { useLang } from "../i18n/LangProvider";

const Navbar = ({ onReserveClick }) => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = [
    { id: "histoire", label: t.nav.histoire },
    { id: "story", label: t.nav.story },
    { id: "menu", label: t.nav.menu },
    { id: "gallery", label: t.nav.gallery },
    { id: "contact", label: t.nav.contact },
  ];

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-obsidian/90 backdrop-blur-xl border-b border-edge/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("hero")}
          className="flex items-baseline gap-2 group"
        >
          <span className="font-serif text-3xl tracking-tight text-ivory group-hover:text-copper transition-colors">
            Grizzly
          </span>
          <span className="hidden sm:inline overline">· La Potence</span>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {items.map((it) => (
            <button
              key={it.id}
              data-testid={`nav-link-${it.id}`}
              onClick={() => scrollTo(it.id)}
              className="text-sm uppercase tracking-[0.18em] text-ivory/80 link-underline"
            >
              {it.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="hidden sm:flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-ivory/70 hover:text-copper transition-colors"
            aria-label="Toggle language"
          >
            <span className={lang === "fr" ? "text-copper" : ""}>FR</span>
            <span className="text-edge">/</span>
            <span className={lang === "en" ? "text-copper" : ""}>EN</span>
          </button>

          <button
            data-testid="nav-reserve-button"
            onClick={onReserveClick}
            className="hidden md:inline-flex btn-primary"
            style={{ padding: "0.7rem 1.4rem", fontSize: "0.72rem" }}
          >
            {t.nav.reserve}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-ivory"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-obsidian/95 backdrop-blur-xl border-t border-edge"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {items.map((it) => (
              <button
                key={it.id}
                data-testid={`mobile-nav-link-${it.id}`}
                onClick={() => scrollTo(it.id)}
                className="text-left font-serif text-2xl text-ivory hover:text-copper transition-colors"
              >
                {it.label}
              </button>
            ))}
            <div className="flex items-center justify-between pt-6 border-t border-edge">
              <button
                data-testid="mobile-lang-toggle"
                onClick={toggle}
                className="text-xs uppercase tracking-[0.2em] text-ivory/70"
              >
                <span className={lang === "fr" ? "text-copper" : ""}>FR</span>
                <span className="text-edge mx-2">/</span>
                <span className={lang === "en" ? "text-copper" : ""}>EN</span>
              </button>
              <button
                data-testid="mobile-reserve-button"
                onClick={() => {
                  setOpen(false);
                  onReserveClick();
                }}
                className="btn-primary"
                style={{ padding: "0.7rem 1.4rem", fontSize: "0.72rem" }}
              >
                {t.nav.reserve}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
